import React, { useMemo, useRef, useState, useEffect } from "react";
import {
  useTable,
  useGlobalFilter,
  useFilters,
  usePagination,
} from "react-table";
import Swal from "sweetalert2";
import * as XLSX from "xlsx";
import { Button } from "react-bootstrap";
import { getAllCajaEntrada, getAllCajaSalida } from "../../../api/InventarioApi";

function DefaultColumnFilter({ column: { filterValue, setFilter } }) {
  return (
    <input
      className="form-control form-control-sm"
      value={filterValue || ""}
      onChange={(e) => setFilter(e.target.value || undefined)}
      placeholder="Filtrar..."
      style={{ minWidth: 80 }}
    />
  );
}

export default function Stock({ refreshTrigger }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const tableRef = useRef(null);

  useEffect(() => { loadData(); }, [refreshTrigger]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [entradas, salidas] = await Promise.all([
        getAllCajaEntrada(),
        getAllCajaSalida(),
      ]);

      const stockMap = {};

      (entradas || []).forEach((e) => {
        const art = (e.articulo || "").trim().toUpperCase();
        if (!art) return;
        if (!stockMap[art]) stockMap[art] = { articulo: art, totalEntrada: 0, totalSalida: 0 };
        stockMap[art].totalEntrada += Number(e.cantidad) || 0;
      });

      (salidas || []).forEach((s) => {
        const art = (s.articulo || "").trim().toUpperCase();
        if (!art) return;
        if (!stockMap[art]) stockMap[art] = { articulo: art, totalEntrada: 0, totalSalida: 0 };
        stockMap[art].totalSalida += Number(s.cantidad) || 0;
      });

      const resultado = Object.values(stockMap)
        .map((it) => ({ ...it, stockActual: it.totalEntrada - it.totalSalida }))
        .sort((a, b) => a.articulo.localeCompare(b.articulo));

      setData(resultado);
    } catch (err) {
      Swal.fire("Error", err.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleExportExcel = () => {
    if (!data.length) return Swal.fire("Sin datos", "No hay datos para exportar", "info");
    const mapped = data.map((it) => ({
      Artículo: it.articulo,
      "Total Entradas": it.totalEntrada,
      "Total Salidas": it.totalSalida,
      "Stock Actual": it.stockActual,
    }));
    const ws = XLSX.utils.json_to_sheet(mapped);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Stock");
    XLSX.writeFile(wb, "Stock.xlsx");
  };

  const columns = useMemo(
    () => [
      { Header: "Artículo", accessor: "articulo", minWidth: 200 },
      {
        Header: "Total Entradas",
        accessor: "totalEntrada",
        minWidth: 120,
        disableFilters: true,
        Cell: ({ value }) => (
          <span className="badge bg-success" style={{ fontSize: "0.85rem", minWidth: 40 }}>{value}</span>
        ),
      },
      {
        Header: "Total Salidas",
        accessor: "totalSalida",
        minWidth: 120,
        disableFilters: true,
        Cell: ({ value }) => (
          <span className="badge bg-danger" style={{ fontSize: "0.85rem", minWidth: 40 }}>{value}</span>
        ),
      },
      {
        Header: "Stock Actual",
        accessor: "stockActual",
        minWidth: 120,
        disableFilters: true,
        Cell: ({ value }) => {
          const bajo = value <= 0;
          return (
            <span
              className={`badge ${bajo ? "bg-warning text-dark" : "bg-primary"}`}
              style={{ fontSize: "0.9rem", fontWeight: 700, minWidth: 40 }}
            >
              {value}
            </span>
          );
        },
      },
    ],
    []
  );

  const defaultColumn = useMemo(() => ({ Filter: DefaultColumnFilter }), []);

  const tableInstance = useTable(
    { columns, data, defaultColumn, initialState: { pageSize: 15 } },
    useFilters,
    useGlobalFilter,
    usePagination
  );

  const {
    getTableProps, getTableBodyProps, headerGroups, page, prepareRow,
    state, setGlobalFilter, gotoPage, pageCount, nextPage, previousPage,
    canNextPage, canPreviousPage, pageOptions,
  } = tableInstance;

  const { globalFilter, pageIndex } = state;

  return (
    <div className="opciones-container">
      <div className="opciones-header">
        <h5>Stock Actual</h5>
        <div className="d-flex gap-2">
          <Button size="sm" variant="outline-secondary" onClick={loadData}>Actualizar</Button>
          <Button size="sm" variant="outline-success" onClick={handleExportExcel}>Exportar Excel</Button>
        </div>
      </div>

      <div className="mb-2 opciones-search">
        <input
          type="text"
          className="form-control"
          placeholder="Buscar artículo..."
          value={globalFilter || ""}
          onChange={(e) => setGlobalFilter(e.target.value)}
        />
      </div>

      {loading ? (
        <div className="loading-spinner">
          <div className="spinner-circle"></div>
          <span style={{ marginLeft: 14, color: "#444", fontSize: 18 }}>Calculando stock...</span>
        </div>
      ) : (
        <div className="tabla-scroll">
          <table {...getTableProps()} className="table table-sm tabla-opciones" ref={tableRef}>
            <thead>
              {headerGroups.map((hg, i) => (
                <React.Fragment key={i}>
                  <tr {...hg.getHeaderGroupProps()} className="header-row">
                    {hg.headers.map((col) => (
                      <th key={col.id}>{col.render("Header")}</th>
                    ))}
                  </tr>
                  <tr className="filter-row">
                    {hg.headers.map((col) => (
                      <th key={`${col.id}-filter`}>
                        {col.canFilter ? col.render("Filter") : null}
                      </th>
                    ))}
                  </tr>
                </React.Fragment>
              ))}
            </thead>
            <tbody {...getTableBodyProps()}>
              {page.map((row, ri) => {
                prepareRow(row);
                return (
                  <tr {...row.getRowProps()} key={ri}>
                    {row.cells.map((cell, ci) => (
                      <td {...cell.getCellProps()} key={ci}>{cell.render("Cell")}</td>
                    ))}
                  </tr>
                );
              })}
              {page.length === 0 && (
                <tr><td colSpan={columns.length} className="text-center text-muted py-3">Sin datos de stock</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <div className="opciones-paginacion">
        <span>Página <strong>{pageIndex + 1} de {pageOptions.length || 1}</strong></span>
        <div>
          <button className="btn btn-outline-primary btn-sm me-1" onClick={() => gotoPage(0)} disabled={!canPreviousPage}>{"<<"}</button>
          <button className="btn btn-outline-primary btn-sm me-1" onClick={() => previousPage()} disabled={!canPreviousPage}>Anterior</button>
          <button className="btn btn-outline-primary btn-sm me-1" onClick={() => nextPage()} disabled={!canNextPage}>Siguiente</button>
          <button className="btn btn-outline-primary btn-sm" onClick={() => gotoPage(pageCount - 1)} disabled={!canNextPage}>{">>"}</button>
        </div>
      </div>
    </div>
  );
}
